import React, { useState } from 'react';
import { round } from '../../../util/util';
import { TextField } from '../TextField/TextField';
import GlobalProps from '../../globalProps';
import classnames from 'classnames';
import './NumberField.css';
import { isNumber } from 'lodash';
import { I18n } from '../../../locales/translate';

interface Props extends GlobalProps {
  value?: number | null;
  decimalDigits?: number;
  onBlur?: (value: number) => void;
  onChange?: (value: number) => void;
  percentage?: boolean;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  displayErrorOnBlur?: boolean;
  actions?: React.ReactNode | React.ReactNodeArray;
}

function formatPercentage(value?: number | null, decimalDigits?: number): string {
  if (!isNumber(value)) {
    return '';
  }
  return round(Number(value) * 100, decimalDigits || 2).toString();
}

export function NumberField({
  value,
  decimalDigits,
  onBlur,
  onChange,
  percentage,
  disabled,
  className,
  style,
  label,
  required,
  displayErrorOnBlur,
  actions,
  id,
}: Props): JSX.Element {
  const [editingValue, setEditingValue] = useState<string | undefined>(undefined);

  const digits = decimalDigits !== undefined ? decimalDigits : 2;

  const format = percentage
    ? formatPercentage
    : (val?: number | null, digs?: number) => (isNumber(val) ? val.toFixed(digs) : '');

  const displayedValue = editingValue !== undefined ? editingValue : format(value, digits);

  function handleOnBlur(): void {
    if (editingValue === undefined) {
      return;
    }

    let returnValue = Number(editingValue.replace(/,/g, '.'));

    if (percentage) {
      returnValue = round(returnValue * 0.01, digits);
    }

    if (!isNaN(returnValue) && onBlur) {
      onBlur(returnValue);
    }
    if (!isNaN(returnValue) && onChange && returnValue !== value) {
      onChange(returnValue);
    }
    setEditingValue(undefined);
  }

  function formatGermanNumbers<T>(value: T): string {
    if (I18n.locale === 'de') {
      return (value as unknown as string).replace(/\./g, ',');
    }
    return value as unknown as string;
  }

  return (
    <div className={classnames('number-field', className)} style={style}>
      <TextField
        label={label}
        value={displayedValue}
        type="text"
        pattern={`^[0-9]{1,}([,.][0-9]{1,${percentage ? 2 : ''}})?$`}
        onFocus={() => setEditingValue(displayedValue)}
        onChange={(val) => setEditingValue(val as string)}
        onBlur={handleOnBlur}
        disabled={disabled}
        required={required}
        displayErrorOnBlur={displayErrorOnBlur}
        formatter={formatGermanNumbers}
        actions={actions}
        name={id}
      />
      {percentage && <span className="number-field__percentage">%</span>}
    </div>
  );
}
